import React, { useState } from "react";
import { api } from "../lib/api";
import { Loader2, Scale } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

export const Calculator = () => {
  const { t } = useT();
  const [method, setMethod] = useState("Sea Freight");
  const [cargoType, setCargoType] = useState("Standard Barrel");
  const [weight, setWeight] = useState("");
  const [cubicFeet, setCubicFeet] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleAppraise = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/calculator", {
        method,
        cargo_type: cargoType,
        weight_lbs: weight ? parseFloat(weight) : 0,
        cubic_feet: cubicFeet ? parseFloat(cubicFeet) : 0,
      });
      setResult(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="appraisal"
      data-testid="calculator-section"
      className="relative"
      style={{ background: "#F5EFE0" }}
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#A88420] mb-3 ornament">
              The Appraisal
            </div>
            <h2 className="font-serif-bv text-4xl sm:text-5xl lg:text-6xl text-[#1B3022] leading-[1.05]">
              Estimate your <span className="italic text-[#D4AF37]">commission</span>.
            </h2>
            <p className="mt-5 text-[#1B3022]/75 max-w-md">
              Transparent rates calculated by manifest type and weight. The Royal Post honors a fair toll.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-[#1B3022]/80">
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF37] mt-1">❖</span>
                <span><strong>Small Box</strong> (under 20 lbs) — flat $25.00</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF37] mt-1">❖</span>
                <span><strong>55-Gallon Barrel</strong> (Sea) — flat $150.00</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF37] mt-1">❖</span>
                <span><strong>Custom Air Freight</strong> — $4.50 / lb</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF37] mt-1">❖</span>
                <span><strong>Custom Sea Freight</strong> — $18.00 / cu.ft</span>
              </li>
            </ul>
          </div>

          <form
            onSubmit={handleAppraise}
            data-testid="calculator-form"
            className="lg:col-span-7 border border-[#1B3022]/20 bg-white/70 backdrop-blur-sm rounded-sm p-8 lg:p-10 shadow-sm"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#1B3022]/60 mb-2 block">
                  {t("calc.method")}
                </label>
                <select
                  data-testid="calc-method"
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  className="w-full px-4 py-3 border border-[#1B3022]/30 bg-white rounded-sm text-[#1B3022] focus:outline-none focus:border-[#D4AF37]"
                >
                  <option>Sea Freight</option>
                  <option>Air Freight</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#1B3022]/60 mb-2 block">
                  {t("calc.cargo_type")}
                </label>
                <select
                  data-testid="calc-cargo"
                  value={cargoType}
                  onChange={(e) => setCargoType(e.target.value)}
                  className="w-full px-4 py-3 border border-[#1B3022]/30 bg-white rounded-sm text-[#1B3022] focus:outline-none focus:border-[#D4AF37]"
                >
                  <option>Small Box</option>
                  <option>Standard Barrel</option>
                  <option>Custom</option>
                </select>
              </div>

              {cargoType === "Custom" && (
                <>
                  <div>
                    <label className="text-[10px] tracking-[0.25em] uppercase text-[#1B3022]/60 mb-2 block">
                      {t("calc.weight")}
                    </label>
                    <input
                      data-testid="calc-weight"
                      type="number"
                      min="0"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      placeholder="e.g. 25"
                      className="w-full px-4 py-3 border border-[#1B3022]/30 bg-white rounded-sm text-[#1B3022] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                  {method === "Sea Freight" && (
                    <div>
                      <label className="text-[10px] tracking-[0.25em] uppercase text-[#1B3022]/60 mb-2 block">
                        {t("calc.volume")}
                      </label>
                      <input
                        data-testid="calc-volume"
                        type="number"
                        min="0"
                        value={cubicFeet}
                        onChange={(e) => setCubicFeet(e.target.value)}
                        placeholder="e.g. 8"
                        className="w-full px-4 py-3 border border-[#1B3022]/30 bg-white rounded-sm text-[#1B3022] focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="calc-submit-btn"
              className="btn-gold rounded-sm px-8 py-4 text-xs mt-7 inline-flex items-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : <Scale size={16} />}
              {t("calc.submit_btn")}
            </button>

            {result && (
              <div
                data-testid="calc-result"
                className="mt-8 border-t border-[#1B3022]/20 pt-6 fade-up"
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A88420]">{t("calc.result_label")}</div>
                <div className="font-serif-bv text-5xl lg:text-6xl text-[#1B3022] tabular mt-1">
                  ${result.estimated_cost.toFixed(2)}
                </div>
                <div className="text-sm text-[#1B3022]/70 mt-2">{result.breakdown}</div>
                <div className="text-xs text-[#1B3022]/50 mt-1 tracking-wide">
                  {result.method} • {result.cargo_type}
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Calculator;
